// Journal des règlements : ce que les clients ont payé, et ce qu'ils doivent
// encore.
//
// Deux usages, deux sorties :
//   1. Pour le COMPTABLE : les règlements en écritures, dans le même format que
//      le journal des ventes — le compte de trésorerie (banque ou caisse) au
//      débit, le compte du client au crédit, la facture en pièce pour que le
//      lettrage se fasse tout seul.
//   2. Pour ROMAIN : ce qui est en attente — facture par facture, le reste dû
//      et le retard — et un CSV lisible dans Excel.
//
// Fonctions PURES : aucune horloge, la date du jour est un paramètre.

import { type CelluleCsv, csvDepuisLignes } from './csv.js';
import {
  type ComptesExport,
  compteClient,
  dateFrancaise,
  type EcritureComptable,
  libelleEcriture,
} from './export-comptable.js';

export interface ReglementExport {
  id: string;
  /** yyyy-mm-dd */
  dateReglement: string;
  /** virement | cheque | carte | especes | prelevement | autre */
  mode: string;
  montant: number;
  reference: string | null;
  factureNumero: string;
  factureKind: string;
  client: string;
  compteClient: string | null;
}

export const MODES_REGLEMENT: Record<string, string> = {
  virement: 'Virement',
  cheque: 'Chèque',
  carte: 'Carte',
  especes: 'Espèces',
  prelevement: 'Prélèvement',
  autre: 'Autre',
};

const arrondi = (n: number): number => Math.round(n * 100) / 100;

/**
 * Les deux écritures d'un règlement. Un remboursement (montant négatif, ou
 * règlement d'un avoir) change de côté.
 */
export function ecrituresReglement(r: ReglementExport, comptes: ComptesExport): EcritureComptable[] {
  const especes = r.mode === 'especes';
  const compteTreso = especes ? comptes.caisse : comptes.banque;
  const journal = especes ? comptes.journalCaisse : comptes.journalBanque;
  const m = arrondi(Math.abs(r.montant));
  const inverse = r.montant < 0 || r.factureKind === 'avoir';
  const mode = MODES_REGLEMENT[r.mode] ?? r.mode;
  const detail = r.reference ? `${mode} ${r.reference}` : mode;
  const base = {
    date: dateFrancaise(r.dateReglement),
    journal,
    libelle: libelleEcriture(r.factureNumero, `${r.client} - ${detail}`),
    piece: r.factureNumero,
  };
  const treso: EcritureComptable = {
    ...base,
    compte: compteTreso,
    type: especes ? 'CAISSE' : 'BANQUE',
    debit: inverse ? null : m,
    credit: inverse ? m : null,
  };
  const client: EcritureComptable = {
    ...base,
    compte: compteClient(comptes, r.compteClient),
    type: 'CLIENT',
    debit: inverse ? m : null,
    credit: inverse ? null : m,
  };
  return [treso, client];
}

export function journalReglements(
  reglements: readonly ReglementExport[],
  comptes: ComptesExport,
): { ecritures: EcritureComptable[]; nb: number; total: number } {
  const ecritures = reglements.flatMap((r) => ecrituresReglement(r, comptes));
  const total = arrondi(
    reglements.reduce((s, r) => s + (r.factureKind === 'avoir' ? -Math.abs(r.montant) : r.montant), 0),
  );
  return { ecritures, nb: reglements.length, total };
}

/** Une facture émise, vue du côté de ce qu'elle a rapporté. */
export interface FactureEnAttente {
  id: string;
  numero: string;
  client: string;
  kind: string;
  statut: string;
  dateEmission: string;
  dateEcheance: string;
  montantTtc: number;
  montantPaye: number;
}

export interface LigneAttente extends FactureEnAttente {
  reste: number;
  /** Jours de retard sur l'échéance ; négatif tant qu'elle n'est pas passée. */
  joursRetard: number;
  enRetard: boolean;
}

const JOUR_MS = 86_400_000;
const jourMs = (iso: string): number => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

/**
 * Ce qui reste à encaisser, facture par facture, la plus en retard d'abord.
 * Les avoirs et les factures annulées par un avoir n'attendent rien ; une
 * facture soldée non plus.
 */
export function attenteReglements(
  factures: readonly FactureEnAttente[],
  aujourdhui: string,
): { lignes: LigneAttente[]; totalReste: number; totalEnRetard: number; nbEnRetard: number } {
  const ref = jourMs(aujourdhui);
  const lignes: LigneAttente[] = [];
  for (const f of factures) {
    if (f.kind === 'avoir' || f.statut === 'annulee_avoir' || f.statut === 'brouillon') continue;
    const reste = arrondi(f.montantTtc - f.montantPaye);
    if (reste <= 0.005) continue;
    const ech = jourMs(f.dateEcheance);
    const joursRetard = Number.isNaN(ech) ? 0 : Math.floor((ref - ech) / JOUR_MS);
    lignes.push({ ...f, reste, joursRetard, enRetard: joursRetard > 0 });
  }
  lignes.sort((a, b) => b.joursRetard - a.joursRetard || a.numero.localeCompare(b.numero));
  const enRetard = lignes.filter((l) => l.enRetard);
  return {
    lignes,
    totalReste: arrondi(lignes.reduce((s, l) => s + l.reste, 0)),
    totalEnRetard: arrondi(enRetard.reduce((s, l) => s + l.reste, 0)),
    nbEnRetard: enRetard.length,
  };
}

/** L'attente en CSV lisible : une ligne par facture, un total en bas. */
export function csvAttente(lignes: readonly LigneAttente[]): string {
  const corps: CelluleCsv[][] = [
    ['Facture', 'Client', 'Émise le', 'Échéance', 'TTC', 'Payé', 'Reste dû', 'Retard (jours)'],
    ...lignes.map((l) => [
      l.numero,
      l.client,
      dateFrancaise(l.dateEmission),
      dateFrancaise(l.dateEcheance),
      l.montantTtc,
      l.montantPaye,
      l.reste,
      l.enRetard ? l.joursRetard : 0,
    ]),
    ['TOTAL', '', '', '', null, null, arrondi(lignes.reduce((s, l) => s + l.reste, 0)), null],
  ];
  return csvDepuisLignes(corps);
}

/** Les règlements en CSV lisible (pour Romain, pas pour le logiciel comptable). */
export function csvReglements(reglements: readonly ReglementExport[], pointes: ReadonlySet<string>): string {
  const corps: CelluleCsv[][] = [
    ['Date', 'Facture', 'Client', 'Mode', 'Référence', 'Montant', 'Pointé'],
    ...reglements.map((r) => [
      dateFrancaise(r.dateReglement),
      r.factureNumero,
      r.client,
      MODES_REGLEMENT[r.mode] ?? r.mode,
      r.reference ?? '',
      r.factureKind === 'avoir' ? -Math.abs(r.montant) : r.montant,
      pointes.has(r.id) ? 'oui' : 'non',
    ]),
    ['TOTAL', '', '', '', '', journalReglements(reglements, { ...COMPTES_VIDES }).total, ''],
  ];
  return csvDepuisLignes(corps);
}

// Des comptes quelconques : le total d'un journal ne dépend pas des comptes.
const COMPTES_VIDES: ComptesExport = {
  journal: '', ventes: '', ventesParKind: {}, client: '', tvaParTaux: {}, tvaDefaut: '',
  clientsIndividuels: false, prefixeClient: '', chiffresClient: 0, banque: '', caisse: '', journalBanque: '', journalCaisse: '',
};
